  (() => {
    if (customElements.get('labels-mega-menu')) return;

    class LabelsMegaMenu extends HTMLElement {
      connectedCallback() {
        this.triggers = [...this.querySelectorAll('[data-mm-trigger]')];
        this.openDelay = Number(this.dataset.openDelay || 80);
        this.closeDelay = Number(this.dataset.closeDelay || 220);
        this.current = null;
        this.tOpen = null;
        this.tClose = null;

        this.triggers.forEach((btn) => {
          btn.addEventListener('mouseenter', () => this.schedule(() => this.open(btn), this.current ? 30 : this.openDelay));
          btn.addEventListener('click', () => (this.current === btn ? this.close() : this.open(btn)));
          btn.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); this.open(btn); this.panelOf(btn).querySelector('a, button, input')?.focus(); }
          });
        });

        this.addEventListener('mouseleave', () => this.schedule(() => this.close(), this.closeDelay));
        this.querySelector('.mm-panels').addEventListener('mouseenter', () => clearTimeout(this.tClose));
        this.querySelector('.mm-bar').addEventListener('mouseenter', () => clearTimeout(this.tClose));
        this.querySelector('[data-mm-overlay]').addEventListener('click', () => this.close());
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.current) { const b = this.current; this.close(); b.focus(); }
        });
        this.addEventListener('focusout', (e) => {
          if (this.current && !this.contains(e.relatedTarget)) this.close();
        });

        this.querySelectorAll('[data-mm-panel]').forEach((p) => this.wirePanel(p));
        this.wireLocalization();
        this.wireCart();
        this.wireProxies();
        this.wireMobile();
        this.wireStickyBar();

        // Theme editor: selecting a panel block opens its panel
        document.addEventListener('shopify:block:select', (e) => {
          const btn = e.target.closest?.('[data-mm-trigger]');
          if (btn && this.contains(btn)) this.open(btn);
        });
        document.addEventListener('shopify:block:deselect', (e) => {
          if (e.target.closest?.('[data-mm-trigger]') && this.contains(e.target)) this.close();
        });
      }

      // Our search / account / cart icons trigger the theme header's own controls
      // (cart drawer, search modal, account popover). Falls back to the link's URL.
      themeHeaders() {
        return [...document.querySelectorAll('.shopify-section-group-header-group')]
          .filter((s) => !s.contains(this) && s.querySelector('header, header-component'));
      }

      findThemeControl(kind) {
        const sel = {
          cart: ['cart-drawer-component button', 'cart-icon button', 'button[aria-controls*="cart" i]', 'button[aria-label*="cart" i]', 'button[aria-label*="winkel" i]', 'button[aria-label*="bag" i]', 'button[class*="cart" i]'],
          search: ['search-button button', 'predictive-search-component button', 'button[aria-controls*="search" i]', 'button[aria-label*="search" i]', 'button[aria-label*="zoek" i]', 'button[class*="search" i]'],
          account: ['account-popover button', 'button[popovertarget*="account" i]', 'button[aria-controls*="account" i]', 'button[aria-label*="account" i]', 'button[class*="account" i]']
        }[kind];
        for (const root of this.themeHeaders()) {
          for (const s of sel) {
            const el = root.querySelector(s);
            if (!el) continue;
            if (kind === 'account') {
              // Only proxy when the theme opens a popover/dialog (rendered above the page);
              // an inline dropdown would stay invisible inside the collapsed theme header.
              const id = el.getAttribute('popovertarget') || el.getAttribute('aria-controls');
              const t = id && document.getElementById(id);
              if (!t || !(t.tagName === 'DIALOG' || t.hasAttribute('popover'))) return null;
            }
            return el;
          }
        }
        return null;
      }

      wireProxies() {
        this.querySelectorAll('[data-mm-proxy]').forEach((a) => {
          a.addEventListener('click', (e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey) return;
            const target = this.findThemeControl(a.dataset.mmProxy);
            if (!target) return; // no theme control found: follow the link
            e.preventDefault();
            this.close();
            target.click();
          });
        });
      }

      schedule(fn, ms) { clearTimeout(this.tOpen); clearTimeout(this.tClose); this.tOpen = setTimeout(fn, ms); }
      panelOf(btn) { return this.querySelector('#' + CSS.escape(btn.getAttribute('aria-controls'))); }

      open(btn) {
        clearTimeout(this.tClose);
        if (this.current === btn) return;
        if (this.current) { this.current.setAttribute('aria-expanded', 'false'); this.panelOf(this.current).hidden = true; }
        btn.setAttribute('aria-expanded', 'true');
        const panel = this.panelOf(btn);
        panel.mmReset?.();
        panel.hidden = false;
        this.current = btn;
        this.classList.add('is-open');
      }

      close() {
        clearTimeout(this.tOpen);
        if (!this.current) return;
        this.current.setAttribute('aria-expanded', 'false');
        this.panelOf(this.current).hidden = true;
        this.current = null;
        this.classList.remove('is-open');
      }

      wirePanel(panel) {
        // A–Z strip
        const az = panel.querySelector('[data-mm-az]');
        if (az) {
          const letters = [...az.querySelectorAll('.mm-letter')];
          const chips = [...az.querySelectorAll('.mm-chip')];
          const pick = (btn) => {
            if (btn.disabled) return;
            letters.forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
            const L = btn.dataset.letter;
            chips.forEach((c) => (c.hidden = c.dataset.letter !== L));
          };
          letters.forEach((b) => ['mouseenter', 'focus', 'click'].forEach((ev) => b.addEventListener(ev, () => pick(b))));
          // Every time the panel opens, start again on the default letter from the block settings
          panel.mmReset = () => {
            const def = letters.find((b) => b.dataset.letter === az.dataset.defaultLetter && !b.disabled) || letters.find((b) => !b.disabled);
            if (def) pick(def);
          };
        }

        // Designers index
        const index = panel.querySelector('[data-mm-index]');
        if (index) {
          const items = [...index.querySelectorAll('[data-mm-v]')];
          const groups = [...index.querySelectorAll('[data-mm-grp]')];
          const q = panel.querySelector('[data-mm-q]');
          const segs = [...panel.querySelectorAll('[data-mm-seg]')];
          const count = panel.querySelector('[data-mm-count]');
          const empty = panel.querySelector('[data-mm-empty]');
          const pvName = panel.querySelector('[data-mm-pv-name]');
          const pvGlyph = panel.querySelector('[data-mm-pv-glyph]');
          const pvW = panel.querySelector('[data-mm-pv-w]');
          const pvM = panel.querySelector('[data-mm-pv-m]');
          const norm = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
          let seg = 'all';

          const filter = () => {
            const term = norm(q?.value || '');
            let shown = 0;
            items.forEach((a) => {
              const okSeg = seg === 'all' || a.dataset[seg] === 'true';
              const okQ = !term || norm(a.dataset.mmV).includes(term);
              a.hidden = !(okSeg && okQ);
              if (!a.hidden) shown++;
            });
            groups.forEach((g) => (g.hidden = !g.querySelector('[data-mm-v]:not([hidden])')));
            if (count) count.textContent = shown;
            if (empty) empty.hidden = shown > 0;
            index.scrollLeft = 0;
          };

          const preview = (a) => {
            items.forEach((i) => i.classList.toggle('is-active', i === a));
            pvName.textContent = a.dataset.mmV;
            pvGlyph.textContent = (norm(a.dataset.mmV).charAt(0) || '#').toUpperCase();
            if (pvW) { pvW.hidden = a.dataset.w !== 'true'; if (a.dataset.wurl) pvW.href = a.dataset.wurl; }
            if (pvM) { pvM.hidden = a.dataset.m !== 'true'; if (a.dataset.murl) pvM.href = a.dataset.murl; }
          };

          items.forEach((a) => { a.addEventListener('mouseenter', () => preview(a)); a.addEventListener('focus', () => preview(a)); });
          q?.addEventListener('input', filter);
          q?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') { const first = items.find((a) => !a.hidden); if (first) window.location.href = first.href; }
          });
          segs.forEach((b) => b.addEventListener('click', () => {
            seg = b.dataset.mmSeg;
            segs.forEach((s) => s.setAttribute('aria-pressed', String(s === b)));
            filter();
          }));
          index.addEventListener('wheel', (e) => {
            if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) { index.scrollLeft += e.deltaY; e.preventDefault(); }
          }, { passive: false });
        }

        // Product tabs
        const tabs = [...panel.querySelectorAll('[data-mm-tab]')];
        if (tabs.length) {
          const grids = [...panel.querySelectorAll('[data-mm-grid]')];
          const viewAll = panel.querySelector('[data-mm-viewall]');
          const show = (b) => {
            const k = b.dataset.mmTab;
            tabs.forEach((t) => t.setAttribute('aria-pressed', String(t === b)));
            grids.forEach((g) => (g.hidden = g.dataset.mmGrid !== k));
            if (viewAll && viewAll.dataset[k + 'url']) viewAll.href = viewAll.dataset[k + 'url'];
          };
          tabs.forEach((b) => { b.addEventListener('click', () => show(b)); b.addEventListener('mouseenter', () => show(b)); });
          const initial = tabs.find((t) => t.getAttribute('aria-pressed') === 'true');
          if (initial) show(initial);
        }
      }

      wireLocalization() {
        const root = this.querySelector('[data-mm-loc]');
        if (!root) return;
        const btn = root.querySelector('[data-mm-loc-btn]');
        const pop = root.querySelector('[data-mm-loc-pop]');
        const form = pop.querySelector('form');
        const inCountry = form.querySelector('[data-mm-loc-country]');
        const inLang = form.querySelector('[data-mm-loc-language]');
        const status = pop.querySelector('[data-mm-loc-status]');
        const q = pop.querySelector('[data-mm-loc-q]');
        const body = pop.querySelector('[data-mm-loc-body]');
        const list = pop.querySelector('[data-mm-loc-list]');
        let opts = [...pop.querySelectorAll('[data-mm-loc-opt]')];
        let groups = [...pop.querySelectorAll('[data-mm-loc-group]')];
        const chips = pop.querySelector('[data-mm-loc-chips]');
        const sugLabel = pop.querySelector('[data-mm-loc-suggest-label]');
        const allLabel = pop.querySelector('[data-mm-loc-all-label]');
        const empty = pop.querySelector('[data-mm-loc-empty]');
        const current = inCountry.value;
        const norm = (t) => (t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
        const esc = (t) => t.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
        let active = -1;

        const isOpen = () => !pop.hidden;
        const home = { parent: pop.parentNode, next: pop.nextSibling };
        const portal = this.querySelector('[data-mm-loc-portal]');
        const mq = window.matchMedia('(max-width: ' + this.dataset.mobileMax + 'px)');
        const inside = (el) => !!el && (root.contains(el) || pop.contains(el));
        // The country list is rendered by sections/labels-mm-countries.liquid and only fetched once the
        // shopper shows interest in the picker; it is cached per storefront path, country and language.
        let listReady = null;
        const loadList = () => {
          if (listReady) return listReady;
          if (!list || !list.dataset.src || opts.length) return (listReady = Promise.resolve());
          const key = 'mm-loc:' + list.dataset.src + ':' + current + ':' + inLang.value;
          let cached = null;
          try { cached = sessionStorage.getItem(key); } catch (e) { /* storage blocked */ }
          const html = cached
            ? Promise.resolve(cached)
            : fetch(list.dataset.src, { credentials: 'same-origin' }).then((r) => {
                if (!r.ok) throw new Error('labels-mm-countries ' + r.status);
                return r.text();
              });
          listReady = html.then((text) => {
            const items = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-mm-loc-items]');
            if (!items) throw new Error('labels-mm-countries: no items');
            if (!cached) { try { sessionStorage.setItem(key, text); } catch (e) { /* quota or blocked */ } }
            list.replaceChildren(...items.children);
            list.removeAttribute('aria-busy');
            opts = [...list.querySelectorAll('[data-mm-loc-opt]')];
            groups = [...list.querySelectorAll('[data-mm-loc-group]')];
            wireOptions();
            if (q && q.value) filter();
          }).catch((err) => {
            listReady = null;
            console.warn(err);
          });
          return listReady;
        };
        ['pointerenter', 'focus', 'touchstart'].forEach((t) => btn.addEventListener(t, loadList, { once: true, passive: true }));
        this.querySelector('[data-mm-mloc]')?.addEventListener('pointerdown', loadList, { once: true, passive: true });

        const open = () => {
          this.close(); // close any mega panel
          loadList();
          if (mq.matches && portal) { portal.appendChild(pop); pop.classList.add('is-sheet'); }
          else if (pop.parentNode !== home.parent) { home.parent.insertBefore(pop, home.next); pop.classList.remove('is-sheet'); }
          pop.hidden = false;
          btn.setAttribute('aria-expanded', 'true');
          // Always open at the top so the suggested (popular) countries are visible first
          if (body) body.scrollTop = 0;
          pop.scrollTop = 0;
          // On phones: no auto-focus, so the keyboard doesn't jump up over the language buttons.
          if (!pop.classList.contains('is-sheet')) requestAnimationFrame(() => (q || pop.querySelector('button'))?.focus());
        };
        const shut = (focusBtn) => {
          if (!isOpen()) return;
          pop.hidden = true;
          btn.setAttribute('aria-expanded', 'false');
          if (focusBtn) btn.focus();
        };
        btn.addEventListener('click', () => (isOpen() ? shut() : open()));
        document.addEventListener('click', (e) => {
          if (!isOpen() || inside(e.target) || e.target.closest?.('[data-mm-mloc]')) return;
          shut();
        });
        pop.addEventListener('keydown', (e) => { if (e.key === 'Escape' && isOpen()) { e.stopPropagation(); shut(!pop.classList.contains('is-sheet')); } });
        root.addEventListener('keydown', (e) => { if (e.key === 'Escape' && isOpen()) { e.stopPropagation(); shut(true); } });
        let pressing = false;
        pop.addEventListener('pointerdown', () => { pressing = true; });
        document.addEventListener('pointerup', () => setTimeout(() => (pressing = false), 400));
        root.addEventListener('focusout', (e) => {
          if (pressing || !isOpen()) return;
          if (!inside(e.relatedTarget)) shut();
        });
        // The mobile sheet closes only via its close button, the backdrop or Escape. Closing on focusout made
        // iOS hide the sheet before a tapped language button could register (iOS doesn't focus tapped buttons).
        this.locOpen = open;

        const submit = (field, value, label) => {
          if (field === 'country' && value === current) { shut(true); return; }
          (field === 'country' ? inCountry : inLang).value = value;
          root.classList.add('is-busy');
          pop.classList.add('is-busy');
          if (status) status.textContent = (field === 'country' ? 'Switching to ' : 'Language: ') + label + '…';
          form.submit();
        };

        // Taps are real submit buttons (language pills, country rows, chips): the browser sends the form itself,
        // so it works the same on every phone. Here we only mirror the choice into the hidden fields and show progress.
        form.addEventListener('submit', (e) => {
          const b = e.submitter;
          if (!b || !b.name) return;
          if ((b.name === 'country_code' && b.value === current) || (b.name === 'language_code' && b.getAttribute('aria-pressed') === 'true')) {
            e.preventDefault(); shut(true); return;
          }
          (b.name === 'country_code' ? inCountry : inLang).value = b.value;
          root.classList.add('is-busy');
          pop.classList.add('is-busy');
          const o = b.name === 'country_code' ? opts.find((x) => x.dataset.mmLocOpt === b.value) : null;
          if (status) status.textContent = (b.name === 'country_code' ? 'Switching to ' + (o ? o.dataset.name : b.value) : 'Language: ' + b.textContent.trim()) + '…';
        });

        // Countries: click option or chip
        const pick = (code) => {
          const o = opts.find((x) => x.dataset.mmLocOpt === code);
          submit('country', code, o ? o.dataset.name : code);
        };
        const wireOptions = () => {
          opts.forEach((o) => {
            o.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(o.dataset.mmLocOpt); } });
          });

          // "Based on your browser" suggestion, first in the chip row
          if (chips) {
            const regions = (navigator.languages || [navigator.language]).map((l) => (l.split('-')[1] || '').toUpperCase()).filter(Boolean);
            const hit = regions.map((r) => opts.find((o) => o.dataset.mmLocOpt === r)).find(Boolean);
            if (hit && hit.dataset.mmLocOpt !== current) {
              chips.querySelector(`[data-mm-loc-pick="${hit.dataset.mmLocOpt}"]`)?.remove();
              const chip = document.createElement('button');
              chip.type = 'submit';
              chip.name = 'country_code';
              chip.value = hit.dataset.mmLocOpt;
              chip.className = 'mm-loc__chip mm-loc__chip--detected';
              chip.innerHTML = hit.querySelector('.mm-flag').outerHTML + esc(hit.dataset.name) + ' <small>· near you</small>';
              chips.prepend(chip);
            }
            const none = !chips.children.length;
            const searching = !!(q && q.value.trim());
            chips.hidden = none || searching;
            sugLabel.hidden = none || searching;
          }
        };
        if (opts.length) wireOptions();
        else if (chips && !chips.children.length) { chips.hidden = true; sugLabel.hidden = true; }

        // Search: name, ISO code, currency code or currency name
        const visible = () => opts.filter((o) => !o.hidden);
        const setActive = (i) => {
          const v = visible();
          opts.forEach((o) => o.classList.remove('is-active'));
          active = Math.max(-1, Math.min(i, v.length - 1));
          if (active >= 0) {
            v[active].classList.add('is-active');
            v[active].scrollIntoView({ block: 'nearest' });
            q.setAttribute('aria-activedescendant', v[active].id);
          } else q?.removeAttribute('aria-activedescendant');
        };
        const filter = () => {
          if (!opts.length) { loadList(); return; }
          const term = norm(q.value);
          const searching = term.length > 0;
          let shown = 0;
          opts.forEach((o) => {
            const label = o.querySelector('[data-mm-loc-label]');
            const name = o.dataset.name;
            const hay = norm(o.dataset.search);
            const ok = !searching || hay.split(/\s+/).some((w) => w.startsWith(term)) || hay.includes(term);
            o.hidden = !ok;
            if (ok) shown++;
            // highlight the matched part of the country name
            const i = searching ? norm(name).indexOf(term) : -1;
            label.innerHTML = i >= 0 ? esc(name.slice(0, i)) + '<mark>' + esc(name.slice(i, i + term.length)) + '</mark>' + esc(name.slice(i + term.length)) : esc(name);
          });
          groups.forEach((g) => (g.hidden = searching || !opts.some((o) => !o.hidden && o.dataset.group === g.dataset.mmLocGroup)));
          if (chips && chips.children.length) { chips.hidden = searching; sugLabel.hidden = searching; }
          allLabel.hidden = searching;
          empty.hidden = shown > 0;
          body.scrollTop = 0;
          setActive(searching ? 0 : -1);
        };
        q?.addEventListener('input', filter);
        q?.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setActive(active + 1); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(active - 1); }
          else if (e.key === 'Enter') {
            e.preventDefault();
            const v = visible();
            const o = v[active >= 0 ? active : 0];
            if (o) pick(o.dataset.mmLocOpt);
          }
        });
      }

      wireMobile() {
        const m = this.querySelector('[data-mm-mobile]');
        if (!m) return;
        const sheet = m.querySelector('[data-mm-sheet]');
        const openBtn = m.querySelector('[data-mm-mopen]');
        const closeBtn = m.querySelector('[data-mm-mclose]');
        const backBtn = m.querySelector('[data-mm-back]');
        const backLabel = m.querySelector('[data-mm-back-label]');
        const dock = m.querySelector('[data-mm-dock]');
        const views = [...m.querySelectorAll('[data-mm-view]')];
        const viewById = (id) => views.find((v) => v.dataset.mmView === id);
        const tabs = [...m.querySelectorAll('[data-mm-gtab]')];
        const roots = [...m.querySelectorAll('[data-mm-groot]')];
        const q = m.querySelector('[data-mm-mq]');
        const results = m.querySelector('[data-mm-results]');
        const resultsList = m.querySelector('[data-mm-results-list]');
        const resultsCount = m.querySelector('[data-mm-results-count]');
        const resultsAll = m.querySelector('[data-mm-results-all]');
        const rootContent = m.querySelector('[data-mm-rootcontent]');
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        let stack = ['root'];
        let gender = m.dataset.defaultGender;
        try { const saved = localStorage.getItem('mm-gender'); if (saved && roots.some((r) => r.dataset.mmGroot === saved)) gender = saved; } catch (_) {}

        const setGender = (g, animate) => {
          gender = g;
          tabs.forEach((t) => t.setAttribute('aria-selected', String(t.dataset.mmGtab === g)));
          roots.forEach((r) => {
            const on = r.dataset.mmGroot === g;
            r.hidden = !on;
            if (on && animate) { r.classList.remove('is-swap'); void r.offsetWidth; r.classList.add('is-swap'); }
          });
          try { localStorage.setItem('mm-gender', g); } catch (_) {}
          if (q && q.value) search();
        };
        tabs.forEach((t) => t.addEventListener('click', () => setGender(t.dataset.mmGtab, true)));
        // swipe left/right on the root view to switch department
        const rootView = viewById('root');
        let sx = 0, sy = 0;
        rootView.addEventListener('touchstart', (e) => { sx = e.touches[0].clientX; sy = e.touches[0].clientY; }, { passive: true });
        rootView.addEventListener('touchend', (e) => {
          const dx = e.changedTouches[0].clientX - sx, dy = e.changedTouches[0].clientY - sy;
          if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5 || !tabs.length) return;
          const i = tabs.findIndex((t) => t.dataset.mmGtab === gender);
          const n = tabs[(i + (dx < 0 ? 1 : -1) + tabs.length) % tabs.length];
          setGender(n.dataset.mmGtab, true);
        });
        setGender(gender, false);

        const show = (id, dir) => {
          views.forEach((v) => { v.hidden = v.dataset.mmView !== id; v.classList.remove('v-fwd', 'v-back'); });
          const v = viewById(id);
          if (!reduce && dir) { void v.offsetWidth; v.classList.add(dir === 'fwd' ? 'v-fwd' : 'v-back'); }
          const atRoot = id === 'root';
          backBtn.hidden = atRoot;
          backLabel.textContent = atRoot ? '' : (v.dataset.backLabel || '');
          if (dir === 'fwd') v.scrollTop = 0;
        };
        m.querySelectorAll('[data-mm-go]').forEach((b) => b.addEventListener('click', () => {
          stack.push(b.dataset.mmGo);
          show(b.dataset.mmGo, 'fwd');
          backBtn.focus({ preventScroll: true });
        }));
        backBtn.addEventListener('click', () => { if (stack.length > 1) { stack.pop(); show(stack[stack.length - 1], 'back'); } });

        const lock = (on) => document.documentElement.classList.toggle('mm-locked', on);
        // Remember where the visitor was (view stack, department, scroll) for this session
        const KEY = 'mm-mobile-pos';
        const remember = m.dataset.remember === 'true';
        const TTL = 30 * 60 * 1000;
        const currentView = () => viewById(stack[stack.length - 1]);
        const savePos = () => {
          if (!remember) return;
          const v = currentView();
          const list = v?.querySelector('[data-mm-azlist]');
          try {
            sessionStorage.setItem(KEY, JSON.stringify({ stack, gender, scroll: v ? v.scrollTop : 0, listScroll: list ? list.scrollTop : 0, t: Date.now() }));
          } catch (_) {}
        };
        const restorePos = () => {
          if (!remember) return false;
          let pos = null;
          try { pos = JSON.parse(sessionStorage.getItem(KEY) || 'null'); } catch (_) {}
          if (!pos || Date.now() - pos.t > TTL || !Array.isArray(pos.stack)) return false;
          const valid = pos.stack.filter((id) => viewById(id));
          if (!valid.length || valid[0] !== 'root') return false;
          if (pos.gender && roots.some((r) => r.dataset.mmGroot === pos.gender)) setGender(pos.gender, false);
          stack = valid;
          const id = stack[stack.length - 1];
          show(id);
          const v = viewById(id);
          requestAnimationFrame(() => {
            v.scrollTop = pos.scroll || 0;
            const list = v.querySelector('[data-mm-azlist]');
            if (list) { list.scrollTop = pos.listScroll || 0; list.dispatchEvent(new Event('scroll')); }
          });
          return true;
        };
        // save when a link inside the menu is followed, and when the page is left
        sheet.addEventListener('click', (e) => { if (e.target.closest('a[href]')) savePos(); }, true);
        window.addEventListener('pagehide', () => { if (!sheet.hidden) savePos(); });

        const openSheet = () => {
          this.close();
          if (!restorePos()) { stack = ['root']; show('root'); }
          sheet.hidden = false;
          sheet.classList.remove('is-closing', 'is-anim');
          void sheet.offsetWidth;
          if (!reduce) sheet.classList.add('is-anim');
          openBtn.setAttribute('aria-expanded', 'true');
          lock(true);
          requestAnimationFrame(() => closeBtn.focus({ preventScroll: true }));
          dock.classList.remove('is-hidden');
        };
        const closeSheet = () => {
          if (sheet.hidden) return;
          savePos();
          openBtn.setAttribute('aria-expanded', 'false');
          lock(false);
          if (reduce) { sheet.hidden = true; return; }
          sheet.classList.remove('is-anim');
          sheet.classList.add('is-closing');
          setTimeout(() => { sheet.hidden = true; sheet.classList.remove('is-closing'); if (q) { q.value = ''; search(); } }, 360);
          openBtn.focus({ preventScroll: true });
        };
        openBtn.addEventListener('click', openSheet);
        // Search / bag inside the open band: fold the menu away before the theme's drawer opens
        m.querySelectorAll('.mm-dock--sheet [data-mm-proxy]').forEach((a) => a.addEventListener('click', () => closeSheet(), true));
        closeBtn.addEventListener('click', closeSheet);
        sheet.addEventListener('keydown', (e) => {
          if (e.key !== 'Escape') return;
          if (stack.length > 1) backBtn.click(); else closeSheet();
        });

        // Search across the active department: categories, sub-categories and designers
        const esc = (t) => t.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
        const norm = (t) => (t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
        const sectionLabels = { designers: m.dataset.lDesigners || 'Designers', categories: m.dataset.lCategories || 'Categories', products: m.dataset.lProducts || 'Products' };
        const highlight = (label, term) => {
          const k = norm(label).indexOf(term);
          return k >= 0 ? esc(label.slice(0, k)) + '<mark style="background:none;color:inherit;text-decoration:underline;text-underline-offset:3px">' + esc(label.slice(k, k + term.length)) + '</mark>' + esc(label.slice(k + term.length)) : esc(label);
        };
        const allItems = [...m.querySelectorAll('[data-m-item]')];
        let productReq = 0, productTimer = null;

        function search() {
          const term = norm(q.value);
          if (!term) { results.hidden = true; rootContent.hidden = false; resultsList.innerHTML = ''; return; }
          const designers = new Map();
          const cats = new Map();
          allItems.forEach((a) => {
            const label = a.textContent.trim();
            const n = norm(label);
            if (!n.includes(term)) return;
            const mine = a.dataset.g === gender;
            const starts = n.startsWith(term) || n.includes(' ' + term);
            if (a.dataset.type === 'designer') {
              // one row per designer; prefer the link of the department that is open
              const prev = designers.get(n);
              if (!prev || (mine && !prev.mine)) designers.set(n, { label, href: a.href, mine, starts });
            } else {
              const tag = [a.dataset.dept, a.dataset.kind].filter(Boolean).join(' · ');
              const key = a.href;
              if (!cats.has(key)) cats.set(key, { label, href: a.href, tag, mine, starts });
            }
          });
          const order = (x, y) => (y.starts - x.starts) || (y.mine - x.mine) || x.label.localeCompare(y.label);
          const d = [...designers.values()].sort(order).slice(0, 8);
          const c = [...cats.values()].sort(order).slice(0, 8);
          let i = 0;
          const row = (h, tag) => `<a class="mm-msub" style="--i:${i++}" href="${h.href}"><span>${highlight(h.label, term)}</span>${tag ? `<small>${esc(tag)}</small>` : ''}</a>`;
          let html = '';
          if (d.length) html += `<p class="mm-h" style="margin:4px 0 4px">${esc(sectionLabels.designers)}</p>` + d.map((h) => row(h, '')).join('');
          if (c.length) html += `<p class="mm-h" style="margin:22px 0 4px">${esc(sectionLabels.categories)}</p>` + c.map((h) => row(h, h.tag)).join('');
          html += '<div data-mm-products></div>';
          resultsList.innerHTML = html;
          resultsCount.textContent = (d.length || c.length) ? '' : '';
          if (resultsAll) resultsAll.href = (window.Shopify?.routes?.root || '/') + 'search?q=' + encodeURIComponent(q.value.trim());
          results.hidden = false;
          rootContent.hidden = true;

          // products via Shopify predictive search
          clearTimeout(productTimer);
          const raw = q.value.trim();
          if (raw.length < 2) return;
          const req = ++productReq;
          productTimer = setTimeout(() => {
            const url = (window.Shopify?.routes?.root || '/') + 'search/suggest.json?q=' + encodeURIComponent(raw) +
              '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last';
            fetch(url, { headers: { Accept: 'application/json' } })
              .then((r) => r.json())
              .then((data) => {
                if (req !== productReq) return;
                const box = resultsList.querySelector('[data-mm-products]');
                const items = data?.resources?.results?.products || [];
                const cur = window.Shopify?.currency?.active || 'EUR';
                const fmt = (v) => { const n = parseFloat(v); if (isNaN(n)) return ''; try { return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: cur, maximumFractionDigits: n % 1 ? 2 : 0 }).format(n); } catch (_) { return v; } };
                items.forEach((p) => (p.price_formatted = fmt(p.price)));
                if (!box) return;
                if (!items.length) {
                  if (!d.length && !c.length) box.innerHTML = `<p class="mm-h" style="margin:4px 0">${esc(m.dataset.lEmpty || 'No results')}</p>`;
                  return;
                }
                box.innerHTML = `<p class="mm-h" style="margin:22px 0 8px">${esc(sectionLabels.products)}</p>` + items.map((p) => {
                  const img = p.featured_image?.url || p.image || '';
                  const src = img ? img + (img.includes('?') ? '&' : '?') + 'width=120' : '';
                  return `<a class="mm-mprod" href="${p.url}">
                    <span class="mm-mprod__img">${src ? `<img src="${src}" alt="" loading="lazy" width="48" height="60">` : ''}</span>
                    <span class="mm-mprod__txt"><small>${esc(p.vendor || '')}</small><span>${highlight(p.title || '', term)}</span></span>
                    <span class="mm-mprod__price">${esc(p.price_formatted || '')}</span>
                  </a>`;
                }).join('');
              })
              .catch(() => {});
          }, 220);
        }
        q?.addEventListener('input', search);
        q?.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); const first = resultsList.querySelector('a'); window.location.href = first ? first.href : resultsAll.href; } });

        // Country row opens the picker as a bottom sheet
        m.querySelector('[data-mm-mloc]')?.addEventListener('click', () => this.locOpen?.());

        // A–Z scrubber
        m.querySelectorAll('[data-mm-az-m]').forEach((az) => {
          const list = az.querySelector('[data-mm-azlist]');
          const rail = az.querySelector('[data-mm-rail]');
          const bubble = az.querySelector('[data-mm-bubble]');
          const btns = [...rail.querySelectorAll('button')];
          const grps = [...list.querySelectorAll('.mm-azm__grp')];
          let last = null, hideT = null, dragging = false;
          const mark = (L) => {
            btns.forEach((b) => b.setAttribute('aria-current', String(b.dataset.letter === L)));
            grps.forEach((g) => g.classList.toggle('is-current', g.dataset.letter === L));
          };
          const jump = (b, haptic) => {
            if (!b || b.disabled) return;
            const L = b.dataset.letter;
            const g = grps.find((x) => x.dataset.letter === L);
            if (g) list.scrollTop = g.offsetTop;
            bubble.firstElementChild.textContent = L;
            bubble.style.top = (rail.offsetTop + b.offsetTop + b.offsetHeight / 2 - 36) + 'px';
            bubble.hidden = false;
            clearTimeout(hideT);
            if (L !== last) { if (haptic && navigator.vibrate) navigator.vibrate(4); last = L; }
            mark(L);
          };
          const at = (y) => {
            const r = rail.getBoundingClientRect();
            const i = Math.max(0, Math.min(btns.length - 1, Math.floor(((y - r.top) / r.height) * btns.length)));
            // snap to the nearest letter that has designers
            for (let d = 0; d < btns.length; d++) {
              if (btns[i + d] && !btns[i + d].disabled) return btns[i + d];
              if (btns[i - d] && !btns[i - d].disabled) return btns[i - d];
            }
            return null;
          };
          const end = () => { if (!dragging) return; dragging = false; hideT = setTimeout(() => (bubble.hidden = true), 450); };
          rail.addEventListener('pointerdown', (e) => { dragging = true; try { rail.setPointerCapture(e.pointerId); } catch (_) {} jump(at(e.clientY), true); e.preventDefault(); });
          rail.addEventListener('pointermove', (e) => { if (dragging) jump(at(e.clientY), true); });
          rail.addEventListener('pointerup', end);
          rail.addEventListener('pointercancel', end);
          btns.forEach((b) => b.addEventListener('click', () => { jump(b, false); hideT = setTimeout(() => (bubble.hidden = true), 600); }));
          list.addEventListener('scroll', () => {
            if (dragging) return;
            const top = list.scrollTop + 24;
            let cur = grps[0];
            for (const g of grps) { if (g.offsetTop <= top) cur = g; else break; }
            if (cur) mark(cur.dataset.letter);
          }, { passive: true });
          if (grps[0]) mark(grps[0].dataset.letter);
        });

        // Dock hides while scrolling down, comes back when scrolling up.
        // Chrome on iOS shows its toolbars as soon as a navigation starts; the shrinking viewport shifts
        // scrollY, which must not slide the dock in while the page is being left. Same for any other
        // viewport resize (toolbars, rotation): that is not the visitor scrolling up.
        let lastY = window.scrollY;
        let leaving = false;
        let resizedAt = 0;
        const markResize = () => { resizedAt = performance.now(); lastY = window.scrollY; };
        window.addEventListener('resize', markResize, { passive: true });
        window.visualViewport?.addEventListener('resize', markResize, { passive: true });
        let leaveTimer = 0;
        const leave = () => {
          leaving = true;
          clearTimeout(leaveTimer);
          leaveTimer = setTimeout(() => { leaving = false; lastY = window.scrollY; }, 3000);
        };
        document.addEventListener('click', (e) => {
          const a = e.target instanceof Element ? e.target.closest('a[href]') : null;
          if (!e.defaultPrevented && a && !a.target && !a.hasAttribute('download') && !a.getAttribute('href').startsWith('#')) leave();
        });
        window.addEventListener('beforeunload', leave);
        window.addEventListener('pagehide', leave);
        window.addEventListener('pageshow', () => { leaving = false; lastY = window.scrollY; });
        window.addEventListener('scroll', () => {
          const y = window.scrollY;
          if (leaving || performance.now() - resizedAt < 400) { lastY = y; return; }
          if (Math.abs(y - lastY) < 8) return;
          dock.classList.toggle('is-hidden', y > lastY && y > 240);
          lastY = y;
        }, { passive: true });

        // PDP: logo bar is out of view at the top of the page, slides in once the visitor scrolls down
        // and leaves again when the product photo has scrolled out of view
        const reveal = m.querySelector('[data-mm-reveal]');
        if (reveal) {
          let photo = null;
          const syncReveal = () => {
            photo = photo || document.querySelector('.labels-gallery');
            reveal.classList.toggle('is-revealed', window.scrollY > 24 && (!photo || photo.getBoundingClientRect().bottom > 0));
          };
          window.addEventListener('scroll', syncReveal, { passive: true });
          window.addEventListener('pageshow', syncReveal);
          syncReveal();
        }
      }

      // Desktop bar hides while scrolling down, comes back when scrolling up
      wireStickyBar() {
        const host = this.closest('.shopify-section');
        if (!host || this.dataset.sticky !== 'scroll-up') return;
        const mq = window.matchMedia('(max-width: ' + this.dataset.mobileMax + 'px)');
        const pop = this.querySelector('[data-mm-loc-pop]');
        const show = () => host.classList.remove('mm-is-hidden');
        let lastY = window.scrollY;
        window.addEventListener('scroll', () => {
          const y = window.scrollY;
          if (Math.abs(y - lastY) < 8) return;
          const busy = mq.matches || this.current || (pop && !pop.hidden);
          host.classList.toggle('mm-is-hidden', !busy && y > lastY && y > host.offsetHeight * 2);
          lastY = y;
        }, { passive: true });
        this.addEventListener('focusin', show);
        mq.addEventListener('change', show);
      }

      wireCart() {
        const badges = [...this.querySelectorAll('[data-mm-cart-count]')];
        const link = this.querySelector('[data-mm-cart]');
        if (!badges.length) return;
        const set = (n) => { badges.forEach((b) => { b.textContent = n; b.hidden = !n; }); link?.setAttribute('aria-label', `Bag (${n})`); };
        const refresh = () => fetch((window.Shopify?.routes?.root || '/') + 'cart.js', { headers: { Accept: 'application/json' } })
          .then((r) => r.json()).then((c) => set(c.item_count)).catch(() => {});
        ['cart:update', 'cart:updated', 'cart:refresh'].forEach((ev) => document.addEventListener(ev, (e) => {
          const n = e?.detail?.resource?.item_count ?? e?.detail?.data?.itemCount ?? e?.detail?.cart?.item_count;
          typeof n === 'number' ? set(n) : refresh();
        }));
      }
    }

    customElements.define('labels-mega-menu', LabelsMegaMenu);
  })();
